import { useDisplayTz } from "./useDisplayTz";
import { useCommunityTzContext } from "./useCommunityTz";

export interface ActiveDisplayTz {
  /** The IANA zone tz-YES surfaces are rendering in RIGHT NOW. Never empty. */
  zone: string;
  /** Which preference actually decided `zone`. */
  source: "personal" | "community";
  /** The community whose zone won. Defined only when `source === "community"`. */
  communityName?: string;
}

/**
 * The EFFECTIVE display zone — the one times are actually being rendered in — plus WHY.
 *
 * This exists because an indicator that reports the wrong zone is worse than no indicator:
 * ProfileMenu used to advertise `useDisplayTz()` (the personal chain) while community-mode
 * surfaces rendered in `useDisplayTz(useCommunityTz())`. Both now resolve through this one
 * hook, so the advertised zone and the rendered zone cannot disagree.
 *
 * `source` is derived from the RESULT, not from the mode flag: resolveDisplayTz rejects an
 * invalid community zone and falls through to the personal chain, and useCommunityTz already
 * returns undefined for "All communities" and for a community with no stored zone. Reading the
 * mode alone would claim "community" in all three cases while the app rendered personal.
 */
export function useActiveDisplayTz(): ActiveDisplayTz {
  const { tz: communityTz, communityName } = useCommunityTzContext();
  const zone = useDisplayTz(communityTz);
  return communityTz && zone === communityTz
    ? { zone, source: "community", communityName }
    : { zone, source: "personal" };
}
